import type { ReactNode } from 'react';
import { ThemeToggle } from '@components/ThemeToggle';
import { AuthBackdrop } from '@components/AuthBackdrop';
import { AuthGlobe } from '@components/AuthGlobe';

/**
 * The split sign-in shell: orbiting globe on one side, form card on the other.
 *
 * Shared by the tenant sign-in and the platform administrator sign-in so the
 * two stay identical by construction rather than by copy. Whatever is passed as
 * `children` is dropped into the card and owns its own headings and fields —
 * this component contributes no form markup at all.
 *
 * Responsive behaviour, which is the fiddly part:
 *  - From lg the page is pinned to exactly one viewport height, split in two,
 *    and each column manages its own overflow, so the decorative layers can
 *    never push the page taller than the screen.
 *  - Below lg the globe is dropped entirely (the column would be too narrow to
 *    read), the brand moves from the page corner into the normal flow above the
 *    card — as an absolute corner mark it overlapped the card on small screens —
 *    and the page becomes an ordinary scrolling column so the form always stays
 *    reachable on a short viewport.
 */
export function AuthSplitScreen({ children }: { children: ReactNode }) {
  return (
    <div className="auth-ground relative min-h-screen overflow-x-hidden lg:flex lg:h-screen lg:min-h-0 lg:overflow-hidden">
      <AuthBackdrop />

      {/* Corner brand, large screens only. Below lg it renders in flow instead
          (see the form column) so it can't sit on top of the card. */}
      <div className="absolute left-6 top-6 z-30 hidden sm:left-8 sm:top-8 lg:block">
        <img src="/assets/logos/logo-color.png" alt="CentraOne" className="h-9 dark:hidden" />
        <img src="/assets/logos/logo-white.png" alt="CentraOne" className="hidden h-9 dark:block" />
        <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400">Service Desk Platform</p>
      </div>

      <div className="fixed right-4 top-4 z-30">
        <ThemeToggle />
      </div>

      {/*
       * The globe spans the midline rather than living inside the left column,
       * so its right half continues underneath the card. `pointer-events-none`
       * here; the orbit nodes re-enable their own hit area from the animation
       * loop.
       */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 hidden -translate-x-1/2 -translate-y-1/2 lg:block">
        <AuthGlobe />
      </div>

      {/* Left half: nothing but the caption. Transparent to the pointer so the
          orbit nodes behind it stay hoverable. */}
      <div className="pointer-events-none relative z-10 hidden lg:block lg:h-screen lg:w-1/2">
        <p className="auth-viz-caption absolute bottom-8 left-10 max-w-[16rem]">
          <span className="block text-sm font-semibold text-gray-900 dark:text-white">
            One connected service desk
          </span>
          <span className="mt-1.5 block text-xs leading-relaxed text-gray-600 dark:text-gray-400">
            Tickets, customers, agents, automation and analytics — every part of the platform
            orbiting a single source of truth.
          </span>
        </p>
      </div>

      <div className="relative z-10 flex min-h-screen w-full flex-col justify-center px-4 py-10 sm:px-8 lg:h-screen lg:min-h-0 lg:w-1/2 lg:justify-start lg:overflow-y-auto lg:p-0">
        {/* In-flow brand, small screens only. */}
        <div className="mb-8 text-center lg:hidden">
          <img
            src="/assets/logos/logo-color.png"
            alt="CentraOne"
            className="mx-auto h-9 dark:hidden"
          />
          <img
            src="/assets/logos/logo-white.png"
            alt="CentraOne"
            className="mx-auto hidden h-9 dark:block"
          />
          <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400">Service Desk Platform</p>
        </div>

        {/* From lg the card is flush to the right edge (`lg:mr-0` against the
            auto left margin) and runs the full height, so it reaches every
            screen edge on its side. Its width is set in viewport units rather
            than pixels so the left edge lands at the same point on the globe at
            any screen size — a fixed pixel width would drift across it.
            `flex-1` on the card and `my-auto` on the contents: the fields stay
            optically centred while the card fills the screen, and on a short
            viewport the auto margins collapse so nothing is clipped out of
            reach above the scroll.
            Below lg the card is only as tall as its form and the column
            centres it: stretched to the page height it was ~850px of card
            around a 380px form. The contents are capped at 440px so the
            fields do not run to 650px on a 1920 screen. */}
        <div className="auth-form-card relative z-10 mx-auto flex w-full max-w-[430px] flex-none flex-col px-5 py-8 sm:px-10 sm:py-10 lg:mr-0 lg:max-w-[38vw] lg:flex-1 lg:py-14">
          <div className="mx-auto my-auto w-full max-w-[440px]">{children}</div>
        </div>

        <span className="auth-sysline absolute bottom-6 right-6 hidden lg:block" aria-hidden="true">
          CentraOne // Service Desk
        </span>
      </div>
    </div>
  );
}
